import { Helmet } from 'react-helmet-async';
import { SITE_CONFIG, PAGE_SEO, ORGANIZATION_SCHEMA, PROFESSIONAL_SERVICE_SCHEMA } from '../../config/seo';

/**
 * SEO Head Component
 * 
 * Renders all SEO-related meta tags, Open Graph, Twitter Cards, and JSON-LD.
 * 
 * @param {Object} props
 * @param {string} props.page - Page key from PAGE_SEO config
 * @param {string} [props.title] - Override title
 * @param {string} [props.description] - Override description
 * @param {boolean} [props.noIndex] - Force noindex
 * @param {Object} [props.schema] - Additional JSON-LD schema
 * @param {Array} [props.faqSchema] - FAQ schema data
 * @param {Array} [props.breadcrumbs] - Breadcrumb items
 */
export const SEOHead = ({ 
  page, 
  title: titleOverride, 
  description: descOverride,
  noIndex = false,
  schema = null,
  faqSchema = null,
  breadcrumbs = null,
}) => {
  const pageSeo = PAGE_SEO[page] || {};
  
  const title = titleOverride || pageSeo.title || SITE_CONFIG.defaultTitle;
  const description = descOverride || pageSeo.description || SITE_CONFIG.defaultDescription;
  const canonical = pageSeo.canonical ? `${SITE_CONFIG.siteUrl}${pageSeo.canonical}` : SITE_CONFIG.siteUrl;
  const shouldNoIndex = noIndex || pageSeo.noIndex;
  
  return (
    <Helmet>
      {/* Basic Meta */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      
      {/* Robots */}
      {shouldNoIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}
      
      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_CONFIG.siteName} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:locale" content={SITE_CONFIG.locale} />
      <meta property="og:image" content={SITE_CONFIG.defaultImage} />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={SITE_CONFIG.twitterHandle} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={SITE_CONFIG.defaultImage} />
      
      {/* Organization Schema (on all pages) */}
      <script type="application/ld+json">
        {JSON.stringify(ORGANIZATION_SCHEMA)}
      </script>
      
      {/* Professional Service Schema (home page only via page prop) */}
      {page === 'home' && (
        <script type="application/ld+json">
          {JSON.stringify(PROFESSIONAL_SERVICE_SCHEMA)}
        </script>
      )}
      
      {/* Additional Schema */}
      {schema && (
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      )}
      
      {/* FAQ Schema */}
      {faqSchema && (
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqSchema.map(faq => ({
              '@type': 'Question',
              name: faq.question,
              acceptedAnswer: {
                '@type': 'Answer',
                text: faq.answer,
              },
            })),
          })}
        </script>
      )}
      
      {/* Breadcrumb Schema */}
      {breadcrumbs && (
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: breadcrumbs.map((item, index) => ({
              '@type': 'ListItem',
              position: index + 1,
              name: item.name,
              item: `${SITE_CONFIG.siteUrl}${item.url}`,
            })),
          })}
        </script>
      )}
    </Helmet>
  );
};

export default SEOHead;
